import { ref, watch, type ComputedRef, type Ref } from 'vue'
import * as THREE from 'three'
import type { SceneManager } from '@/three/SceneManager'
import type { Point3D, SceneObject } from '@/types/scene'
import type { useSceneStore, useSelectionStore } from '@/stores/scene'
import type { HistoryManager } from '@/three/HistoryManager'
import { getClickWorldPosition, type ClickGroundOptions } from '@/three/DragHandler'
import { findNearestPipePortByXZ } from '@/three/pipePorts'
import { buildOrthogonalPipeRoute } from '@/utils/pipeRoute'

type SceneStore = ReturnType<typeof useSceneStore>
type SelectionStore = ReturnType<typeof useSelectionStore>

const POLYLINE_TYPES = new Set(['infra_road_polyline', 'infra_wall_polyline', 'infra_river', 'pipe_polyline'])

interface TaggedMesh extends THREE.Mesh {
  userData: {
    objectId?: string
    waypointIdx?: number
    midpointIdx?: number
    extendAt?: 'start' | 'end'
    polyHandle?: boolean
    [k: string]: any
  }
}

export interface PolylineHandlesDeps {
  getSm: () => SceneManager | undefined
  sceneStore: SceneStore
  selectionStore: SelectionStore
  histManager: HistoryManager
  interactionReadonly: ComputedRef<boolean>
  currentFloorId: Ref<string>
  selectedId: Ref<string | null>
}

function snapOpts(deps: PolylineHandlesDeps): ClickGroundOptions {
  const g = deps.sceneStore.activeScene()?.scene?.gridSize ?? 1
  const floor = deps.sceneStore.activeFloors().find(f => f.id === deps.currentFloorId.value)
  return { gridSize: g, groundY: floor?.elevation ?? 0 }
}

function getGroundY(deps: PolylineHandlesDeps) {
  const floor = deps.sceneStore.activeFloors().find(f => f.id === deps.currentFloorId.value)
  return floor?.elevation ?? 0
}

function parseWaypoints(obj: SceneObject): Point3D[] {
  const raw = obj.params?.waypoints
  if (!Array.isArray(raw)) return []
  const out: Point3D[] = []
  for (const p of raw) {
    const x = Number(p?.x)
    const y = Number(p?.y)
    const z = Number(p?.z)
    if (Number.isFinite(x) && Number.isFinite(y) && Number.isFinite(z)) {
      out.push({ x, y, z })
    }
  }
  return out
}

function toWorldWaypoint(obj: SceneObject, p: Point3D): Point3D {
  return {
    x: (obj.position?.[0] ?? 0) + p.x,
    y: (obj.position?.[1] ?? 0) + p.y,
    z: (obj.position?.[2] ?? 0) + p.z,
  }
}

export function createPolylineObjectHandles(deps: PolylineHandlesDeps) {
  const handleRoot = new THREE.Group()
  handleRoot.name = '__polylineHandles__'
  const selectedWaypointIdx = ref<number | null>(null)

  const dragState = {
    active: false,
    objectId: '',
    waypointIdx: -1,
    start: new THREE.Vector3(),
    original: [] as Point3D[],
  }
  let pendingEndpointReconnect: null | {
    endpoint: 'start' | 'end'
    objectId: string
    portId: string
  } = null
  let skipNextClickHandleEdit = false

  function ensureRoot() {
    const sm = deps.getSm()
    if (!sm) return
    if (!sm.scene.getObjectByName(handleRoot.name)) {
      sm.scene.add(handleRoot)
    }
  }

  function clearRoot() {
    while (handleRoot.children.length) {
      const c = handleRoot.children.pop()
      if (!c) break
      if (c instanceof THREE.Mesh) {
        c.geometry.dispose()
        const m = c.material
        if (Array.isArray(m)) m.forEach(mm => mm.dispose())
        else m.dispose()
      }
    }
  }

  function syncPolylineHandles() {
    ensureRoot()
    clearRoot()
    const id = deps.selectedId.value
    if (!id) {
      selectedWaypointIdx.value = null
      deps.getSm()?.markDirty()
      return
    }
    const obj = deps.sceneStore.activeObjects().find(o => o.id === id)
    if (!obj || !POLYLINE_TYPES.has(obj.type)) {
      selectedWaypointIdx.value = null
      deps.getSm()?.markDirty()
      return
    }
    const wps = parseWaypoints(obj)
    const isClosed = obj.params?.closed === true
    for (let i = 0; i < wps.length; i++) {
      const wp = toWorldWaypoint(obj, wps[i]!)
      const sel = selectedWaypointIdx.value === i
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(sel ? 0.44 : 0.36, 14, 12),
        new THREE.MeshBasicMaterial({ color: sel ? 0xf59e0b : 0xfbbf24 })
      ) as TaggedMesh
      m.position.set(wp.x, wp.y, wp.z)
      m.userData.objectId = obj.id
      m.userData.waypointIdx = i
      m.userData.polyHandle = true
      handleRoot.add(m)
    }

    // 中点插点手柄：点击后在该段中间插入一个新点
    const segCount = isClosed ? wps.length : wps.length - 1
    for (let i = 0; i < segCount; i++) {
      const j = isClosed ? (i + 1) % wps.length : i + 1
      const a = toWorldWaypoint(obj, wps[i]!)
      const b = toWorldWaypoint(obj, wps[j]!)
      const mid = new THREE.Mesh(
        new THREE.SphereGeometry(0.26, 12, 10),
        new THREE.MeshBasicMaterial({ color: 0x34d399, opacity: 0.85, transparent: true })
      ) as TaggedMesh
      mid.position.set((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2)
      mid.userData.objectId = obj.id
      mid.userData.midpointIdx = i
      mid.userData.polyHandle = true
      handleRoot.add(mid)
    }

    // 首尾延长手柄：点击后在端点外侧新增一个点
    const extensionDistance = Math.max(1, deps.sceneStore.activeScene()?.scene?.gridSize ?? 1) * 2
    if (!isClosed && wps.length >= 2) {
      const first = toWorldWaypoint(obj, wps[0]!)
      const second = toWorldWaypoint(obj, wps[1]!)
      const last = toWorldWaypoint(obj, wps[wps.length - 1]!)
      const prev = toWorldWaypoint(obj, wps[wps.length - 2]!)

      const dirStart = new THREE.Vector3(first.x - second.x, 0, first.z - second.z)
      if (dirStart.lengthSq() > 1e-6) {
        dirStart.normalize().multiplyScalar(extensionDistance)
        const addStart = new THREE.Mesh(
          new THREE.BoxGeometry(0.48, 0.48, 0.48),
          new THREE.MeshBasicMaterial({ color: 0x22d3ee, opacity: 0.9, transparent: true })
        ) as TaggedMesh
        addStart.position.set(first.x + dirStart.x, first.y, first.z + dirStart.z)
        addStart.userData.objectId = obj.id
        addStart.userData.extendAt = 'start'
        addStart.userData.polyHandle = true
        handleRoot.add(addStart)
      }

      const dirEnd = new THREE.Vector3(last.x - prev.x, 0, last.z - prev.z)
      if (dirEnd.lengthSq() > 1e-6) {
        dirEnd.normalize().multiplyScalar(extensionDistance)
        const addEnd = new THREE.Mesh(
          new THREE.BoxGeometry(0.48, 0.48, 0.48),
          new THREE.MeshBasicMaterial({ color: 0x22d3ee, opacity: 0.9, transparent: true })
        ) as TaggedMesh
        addEnd.position.set(last.x + dirEnd.x, last.y, last.z + dirEnd.z)
        addEnd.userData.objectId = obj.id
        addEnd.userData.extendAt = 'end'
        addEnd.userData.polyHandle = true
        handleRoot.add(addEnd)
      }
    }
    deps.getSm()?.markDirty()
  }

  watch(
    () => {
      const id = deps.selectedId.value
      const o = id ? deps.sceneStore.activeObjects().find(x => x.id === id) : null
      if (!o || !POLYLINE_TYPES.has(o.type)) return ''
      return `${o.id}:${JSON.stringify(o.position ?? [])}:${JSON.stringify(o.params?.waypoints ?? [])}`
    },
    () => syncPolylineHandles(),
    { flush: 'post' }
  )

  function pickHandle(ev: MouseEvent) {
    const sm = deps.getSm()
    if (!sm) return null
    const rect = sm.renderer.domElement.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((ev.clientX - rect.left) / rect.width) * 2 - 1,
      -((ev.clientY - rect.top) / rect.height) * 2 + 1
    )
    const ray = new THREE.Raycaster()
    ray.setFromCamera(ndc, sm.camera)
    const hits = ray.intersectObjects(handleRoot.children, true)
    return hits[0]?.object as TaggedMesh | undefined
  }

  function handlePolylineHandleClick(ev: MouseEvent) {
    if (deps.interactionReadonly.value) return false
    if (skipNextClickHandleEdit) {
      skipNextClickHandleEdit = false
      return true
    }
    const hit = pickHandle(ev)
    if (!hit?.userData?.objectId) return false
    const obj = deps.sceneStore.activeObjects().find(o => o.id === hit.userData.objectId)
    if (!obj || !POLYLINE_TYPES.has(obj.type)) return false

    if (typeof hit.userData.midpointIdx === 'number') {
      const wps = parseWaypoints(obj)
      const isClosed = obj.params?.closed === true
      const i = hit.userData.midpointIdx
      const a = wps[i]
      const b = isClosed ? wps[(i + 1) % wps.length] : wps[i + 1]
      if (!a || !b) return false
      const inserted = {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        z: (a.z + b.z) / 2,
      }
      const next = [...wps]
      next.splice(i + 1, 0, inserted)
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      deps.sceneStore.updateObject(obj.id, { params: { ...obj.params, waypoints: next } })
      selectedWaypointIdx.value = i + 1
      syncPolylineHandles()
      return true
    }

    if (hit.userData.extendAt) {
      const wps = parseWaypoints(obj)
      if (wps.length < 2) return false
      const worldNew = {
        x: hit.position.x,
        y: hit.position.y,
        z: hit.position.z,
      }
      const localNew = {
        x: worldNew.x - (obj.position?.[0] ?? 0),
        y: worldNew.y - (obj.position?.[1] ?? 0),
        z: worldNew.z - (obj.position?.[2] ?? 0),
      }
      const next = [...wps]
      if (hit.userData.extendAt === 'start') {
        next.unshift(localNew)
        selectedWaypointIdx.value = 0
      } else {
        next.push(localNew)
        selectedWaypointIdx.value = next.length - 1
      }
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      deps.sceneStore.updateObject(obj.id, { params: { ...obj.params, waypoints: next } })
      syncPolylineHandles()
      return true
    }

    if (hit.userData.waypointIdx == null) return false
    selectedWaypointIdx.value = hit.userData.waypointIdx
    syncPolylineHandles()
    return true
  }

  function startDrag(ev: PointerEvent) {
    if (deps.interactionReadonly.value) return false
    const hit = pickHandle(ev)
    if (!hit?.userData?.objectId) return false
    const sm = deps.getSm()
    if (!sm) return false
    const pos = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, snapOpts(deps))
    if (!pos) return false
    const obj = deps.sceneStore.activeObjects().find(o => o.id === hit.userData.objectId)
    if (!obj || !POLYLINE_TYPES.has(obj.type)) return false
    const beginDrag = (waypoints: Point3D[], waypointIdx: number) => {
      dragState.active = true
      dragState.objectId = obj.id
      dragState.waypointIdx = waypointIdx
      dragState.start.set(pos.x, getGroundY(deps), pos.z)
      dragState.original = waypoints.map(p => ({ ...p }))
      selectedWaypointIdx.value = waypointIdx
      syncPolylineHandles()
      return true
    }

    if (typeof hit.userData.midpointIdx === 'number') {
      const wps = parseWaypoints(obj)
      const i = hit.userData.midpointIdx
      const a = wps[i]
      const b = wps[i + 1]
      if (!a || !b) return false
      const inserted = {
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
        z: (a.z + b.z) / 2,
      }
      const next = [...wps]
      next.splice(i + 1, 0, inserted)
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      deps.sceneStore.updateObject(obj.id, { params: { ...obj.params, waypoints: next } })
      skipNextClickHandleEdit = true
      return beginDrag(next, i + 1)
    }

    if (hit.userData.extendAt) {
      const wps = parseWaypoints(obj)
      if (wps.length < 2) return false
      const localNew = {
        x: pos.x - (obj.position?.[0] ?? 0),
        y: pos.y - (obj.position?.[1] ?? 0),
        z: pos.z - (obj.position?.[2] ?? 0),
      }
      const next = [...wps]
      let idx = 0
      if (hit.userData.extendAt === 'start') {
        next.unshift(localNew)
        idx = 0
      } else {
        next.push(localNew)
        idx = next.length - 1
      }
      deps.histManager.push(deps.sceneStore.getEditorSnapshot())
      deps.sceneStore.updateObject(obj.id, { params: { ...obj.params, waypoints: next } })
      skipNextClickHandleEdit = true
      return beginDrag(next, idx)
    }

    if (hit.userData.waypointIdx == null) return false
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    return beginDrag(parseWaypoints(obj), hit.userData.waypointIdx)
  }

  function drag(ev: PointerEvent) {
    if (!dragState.active) return
    const sm = deps.getSm()
    if (!sm) return
    const pos = getClickWorldPosition(ev, sm.renderer.domElement, sm.camera, snapOpts(deps))
    if (!pos) return
    const current = new THREE.Vector3(pos.x, getGroundY(deps), pos.z)
    const dx = current.x - dragState.start.x
    const dz = current.z - dragState.start.z
    const next = dragState.original.map(p => ({ ...p }))
    const cur = deps.sceneStore.activeObjects().find(o => o.id === dragState.objectId)
    if (!cur) return
    const i = dragState.waypointIdx
    if (i >= 0 && i < next.length) {
      next[i] = {
        ...next[i]!,
        x: dragState.original[i]!.x + dx,
        z: dragState.original[i]!.z + dz,
      }
      if (cur.type === 'pipe_polyline' && deps.selectionStore.editorTool === 'pipeConnect' && next.length >= 2) {
        const endpoint = i === 0 ? 'start' : i === next.length - 1 ? 'end' : null
        if (endpoint) {
          const worldX = (cur.position?.[0] ?? 0) + next[i]!.x
          const worldZ = (cur.position?.[2] ?? 0) + next[i]!.z
          const nearPort = findNearestPipePortByXZ(worldX, worldZ, deps.sceneStore.activeObjects(), { excludeId: cur.id, threshold: 3.2 })
          pendingEndpointReconnect = nearPort
            ? { endpoint, objectId: nearPort.objectId, portId: nearPort.portId }
            : null
        } else {
          pendingEndpointReconnect = null
        }
      } else {
        pendingEndpointReconnect = null
      }
    }
    deps.sceneStore.updateObject(dragState.objectId, {
      params: { ...cur.params, waypoints: next },
    })
    syncPolylineHandles()
  }

  function stopDrag() {
    const draggedObjId = dragState.objectId
    if (pendingEndpointReconnect) {
      const obj = deps.sceneStore.activeObjects().find((o) => o.id === draggedObjId)
      const wps = Array.isArray(obj?.params?.waypoints) ? (obj?.params?.waypoints as Point3D[]) : []
      if (obj && obj.type === 'pipe_polyline' && wps.length >= 2) {
        const objPos = obj.position ?? [0, 0, 0]
        const worldWps = wps.map((p) => ({ x: p.x + objPos[0], y: p.y + objPos[1], z: p.z + objPos[2] }))
        const snappedPort = findNearestPipePortByXZ(
          pendingEndpointReconnect.endpoint === 'start' ? worldWps[0]!.x : worldWps[worldWps.length - 1]!.x,
          pendingEndpointReconnect.endpoint === 'start' ? worldWps[0]!.z : worldWps[worldWps.length - 1]!.z,
          deps.sceneStore.activeObjects(),
          { threshold: 3.2 },
        )
        if (snappedPort) {
          const anchorOther = pendingEndpointReconnect.endpoint === 'start' ? worldWps[worldWps.length - 1]! : worldWps[0]!
          const route = buildOrthogonalPipeRoute(
            {
              position: pendingEndpointReconnect.endpoint === 'start'
                ? { x: snappedPort.position.x, y: snappedPort.position.y, z: snappedPort.position.z }
                : anchorOther,
            },
            {
              position: pendingEndpointReconnect.endpoint === 'start'
                ? anchorOther
                : { x: snappedPort.position.x, y: snappedPort.position.y, z: snappedPort.position.z },
            },
            { gridSize: deps.sceneStore.activeScene()?.scene?.gridSize ?? 1, forceOrthogonal: true },
          )
          if (route.waypoints.length >= 2) {
            const newAnchor = route.waypoints[0]!
            const localWaypoints = route.waypoints.map((p) => ({
              x: p.x - newAnchor.x,
              y: p.y - newAnchor.y,
              z: p.z - newAnchor.z,
            }))
            const prevEndpoints = (obj.params as any)?.endpoints ?? {}
            const nextEndpoints = {
              ...prevEndpoints,
              [pendingEndpointReconnect.endpoint]: {
                objectId: pendingEndpointReconnect.objectId,
                portId: pendingEndpointReconnect.portId,
              },
            }
            deps.sceneStore.updateObject(obj.id, {
              position: [newAnchor.x, newAnchor.y, newAnchor.z],
              params: { ...obj.params, waypointSpace: 'local', waypoints: localWaypoints, endpoints: nextEndpoints },
            })
          }
        }
      }
    }
    dragState.active = false
    dragState.objectId = ''
    dragState.waypointIdx = -1
    dragState.original = []
    pendingEndpointReconnect = null
  }

  function deleteSelectedWaypoint() {
    const id = deps.selectedId.value
    if (!id || selectedWaypointIdx.value == null) return false
    const obj = deps.sceneStore.activeObjects().find(o => o.id === id)
    if (!obj || !POLYLINE_TYPES.has(obj.type)) return false
    const wps = parseWaypoints(obj)
    const idx = selectedWaypointIdx.value
    if (wps.length <= 2) return false
    const next = [...wps]
    next.splice(idx, 1)
    deps.histManager.push(deps.sceneStore.getEditorSnapshot())
    deps.sceneStore.updateObject(id, { params: { ...obj.params, waypoints: next } })
    selectedWaypointIdx.value = Math.min(idx, next.length - 1)
    syncPolylineHandles()
    return true
  }

  function disposePolylineHandles() {
    clearRoot()
    handleRoot.parent?.remove(handleRoot)
  }

  return {
    syncPolylineHandles,
    handlePolylineHandleClick,
    startPolylineDrag: startDrag,
    dragPolyline: drag,
    stopPolylineDrag: stopDrag,
    deleteSelectedPolylineWaypoint: deleteSelectedWaypoint,
    selectedPolylineWaypointIdx: selectedWaypointIdx,
    disposePolylineHandles,
  }
}
